import { UpdateBeneficiariesBundleConfigurationInput } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { diffUnordered } from '../diff/diffUnordered';
import { BundleSaveDescriptor } from '../presentationSave.types';
import { createFlatConfigDescriptor } from './flatConfigDescriptor';

interface OverviewIds {
  profileIDs: string[];
  organizationIDs: string[];
}

/**
 * Sorts beneficiary ids into the two edges the input writes them to.
 *
 * The form flattens a configuration's client profiles and client organizations
 * into one array, because the slides treat them alike. The mutation keeps them
 * apart, so a save has to tell them apart again — and the id itself is the only
 * thing that says which is which.
 */
function splitByPulidKind(ids: string[]): OverviewIds {
  const profileIDs: string[] = [];
  const organizationIDs: string[] = [];

  ids.forEach((id) => {
    const kind = getPulidKind(id);
    if (kind === PulidKind.ClientProfile) {
      profileIDs.push(id);
    } else if (kind === PulidKind.ClientOrganization) {
      organizationIDs.push(id);
    } else {
      // Unreachable by construction: the form builds these arrays from the two
      // typed edges. Reported rather than dropped in silence, because a
      // beneficiary that lands here is one the report loses.
      diagnostics.warn(
        `Beneficiary report overview id ${id} is neither a client profile nor a client organization, so it cannot be written`
      );
    }
  });

  return { profileIDs, organizationIDs };
}

/**
 * The beneficiary report bundle: which overviews to include, the years and
 * assumptions they are computed against, and which beneficiaries each overview
 * covers.
 *
 * `beneficiaryAccessCalcMethod` is deliberately absent. The input carries it but
 * the form does not, so there is no round trip for a save to preserve.
 */
export const beneficiaryReportBundleDescriptor: BundleSaveDescriptor =
  createFlatConfigDescriptor({
    field: 'beneficiariesBundleConfiguration',
    read: (bundle) => bundle.beneficiariesBundleConfiguration,
    diff: (previous, next) => {
      const update: UpdateBeneficiariesBundleConfigurationInput = {};

      if (previous.displayName !== next.displayName) {
        if (next.displayName) {
          update.displayName = next.displayName;
        } else {
          update.clearDisplayName = true;
        }
      }
      if (previous.includeHouseholdOverview !== next.includeHouseholdOverview) {
        update.includeHouseholdOverview = next.includeHouseholdOverview;
      }
      if (
        previous.includeIndividualOverviews !== next.includeIndividualOverviews
      ) {
        update.includeIndividualOverviews = next.includeIndividualOverviews;
      }
      if (
        previous.includeDetailedBreakdownsInIndividualOverviews !==
        next.includeDetailedBreakdownsInIndividualOverviews
      ) {
        update.includeDetailedBreakdownsInIndividualOverviews =
          next.includeDetailedBreakdownsInIndividualOverviews;
      }
      // Both years are nullable, and zero is a real year, so these check for
      // null rather than for falsiness.
      if (previous.firstDeathYear !== next.firstDeathYear) {
        if (next.firstDeathYear == null) {
          update.clearFirstDeathYear = true;
        } else {
          update.firstDeathYear = next.firstDeathYear;
        }
      }
      if (previous.secondDeathYear !== next.secondDeathYear) {
        if (next.secondDeathYear == null) {
          update.clearSecondDeathYear = true;
        } else {
          update.secondDeathYear = next.secondDeathYear;
        }
      }
      if (previous.growthProfileId !== next.growthProfileId) {
        if (next.growthProfileId) {
          update.growthProfileID = next.growthProfileId;
        } else {
          update.clearGrowthProfile = true;
        }
      }
      if (previous.estateWaterfallId !== next.estateWaterfallId) {
        if (next.estateWaterfallId) {
          update.estateWaterfallID = next.estateWaterfallId;
        } else {
          update.clearEstateWaterfall = true;
        }
      }

      const individual = diffUnordered(
        previous.individualOverviewIDs,
        next.individualOverviewIDs
      );
      if (individual) {
        const added = splitByPulidKind(individual.addIDs);
        const removed = splitByPulidKind(individual.removeIDs);
        if (added.profileIDs.length > 0) {
          update.addIndividualOverviewProfileIDs = added.profileIDs;
        }
        if (added.organizationIDs.length > 0) {
          update.addIndividualOverviewOrganizationIDs = added.organizationIDs;
        }
        if (removed.profileIDs.length > 0) {
          update.removeIndividualOverviewProfileIDs = removed.profileIDs;
        }
        if (removed.organizationIDs.length > 0) {
          update.removeIndividualOverviewOrganizationIDs =
            removed.organizationIDs;
        }
      }

      const household = diffUnordered(
        previous.householdOverviewIDs,
        next.householdOverviewIDs
      );
      if (household) {
        const added = splitByPulidKind(household.addIDs);
        const removed = splitByPulidKind(household.removeIDs);
        if (added.profileIDs.length > 0) {
          update.addHouseholdOverviewProfileIDs = added.profileIDs;
        }
        if (added.organizationIDs.length > 0) {
          update.addHouseholdOverviewOrganizationIDs = added.organizationIDs;
        }
        if (removed.profileIDs.length > 0) {
          update.removeHouseholdOverviewProfileIDs = removed.profileIDs;
        }
        if (removed.organizationIDs.length > 0) {
          update.removeHouseholdOverviewOrganizationIDs =
            removed.organizationIDs;
        }
      }

      return Object.keys(update).length > 0 ? update : null;
    },
    toPayload: (id, update) => ({
      updateBeneficiariesBundleConfiguration: { id, update },
    }),
  });
